import { useDepartmentSafe } from '@/context/DepartmentContext';
import { useCallStateSafe } from '@/context/CallStateContext';
import { cn } from '@/lib/utils';
import { Wrench, Car } from 'lucide-react';
import { useLocation } from 'react-router-dom';

/**
 * Floating pill toggle for switching between Sales and Service departments.
 *
 * Visible to:
 * - Admins/GMs (always — testing tool)
 * - Cross-trained users with 2+ departments
 *
 * Positioned bottom-right, above BottomNav on mobile.
 * Only renders on /train routes to avoid obstructing other pages.
 */
export const DepartmentSwitcher = () => {
  const ctx = useDepartmentSafe();
  const callState = useCallStateSafe();
  const location = useLocation();

  // Don't render if context not available or user only has one department
  if (!ctx || !ctx.isMultiDepartment) return null;

  // Only show on the training-CALL routes (/train, /train/*) — NOT /training-plan,
  // which also starts with "/train" and would otherwise float the FAB over the
  // My Plan content. The FAB obstructs content on other pages.
  const path = location.pathname;
  if (path !== '/train' && !path.startsWith('/train/')) return null;

  // Active mobile calls own the bottom safe area; keep the call controls unobstructed.
  if (callState?.isCallActive) return null;

  const { department, setDepartment, isAdminOverride } = ctx;

  const toggle = () => {
    setDepartment(department === 'sales' ? 'service' : 'sales');
  };

  const isSales = department === 'sales';

  return (
    <button
      onClick={toggle}
      aria-label={`Switch to ${isSales ? 'Service' : 'Sales'} training`}
      className={cn(
        'fixed right-4 z-[55] flex items-center gap-2 rounded-full px-4 py-2.5 min-h-[44px] min-w-[44px] shadow-lg border transition-all duration-300 touch-manipulation',
        'bottom-[calc(var(--bottom-nav-height)+1rem+env(safe-area-inset-bottom,0px))] md:bottom-5',
        'hover:scale-105 active:scale-95',
        'backdrop-blur-md',
        isSales
          ? 'bg-blue-500/90 border-blue-400/50 text-white hover:bg-blue-600/90'
          : 'bg-orange-500/90 border-orange-400/50 text-white hover:bg-orange-600/90',
      )}
    >
      {isSales ? (
        <Car className="h-4 w-4" />
      ) : (
        <Wrench className="h-4 w-4" />
      )}
      <span className="text-sm font-medium">
        {isSales ? 'Sales' : 'Service'}
      </span>
      {isAdminOverride && (
        <span className="text-[10px] opacity-75 font-normal">Admin</span>
      )}
    </button>
  );
};
